/**
 * Chart.js v2 → v3 Type Compatibility Layer
 *
 * This file provides v2-compatible type definitions that work with Chart.js v3.
 * It allows existing code written for Chart.js 2.x to continue working with
 * minimal modifications.
 */

import {
    ChartType as ChartJsType,
    ChartDataset,
    TooltipOptions,
    LegendOptions,
    ScaleOptions,
    Chart,
} from 'chart.js';

// Re-export Chart.js v3 types that are still compatible
export type { ChartDataset, TooltipOptions, LegendOptions, ScaleOptions, Chart };

/**
 * Position type for legend, title, etc.
 * In v2 this was exported as PositionType, in v3 it's just a union type
 */
export type PositionType = 'top' | 'left' | 'bottom' | 'right' | 'center' | 'chartArea';

/**
 * v2-compatible ChartDataSets type (v3 renamed to ChartDataset)
 * Extended to include v2 properties that were renamed or removed in v3
 */
export interface ChartDataSets {
    // Common properties
    label?: string;
    data?: unknown[];
    type?: string;
    order?: number;
    stack?: string;
    hidden?: boolean;

    // v2 properties (some renamed in v3)
    xAxisID?: string; // v2: xAxisID, v3: still xAxisID but default changed
    yAxisID?: string; // v2: yAxisID, v3: still yAxisID but default changed
    lineTension?: number; // v2: lineTension, v3: tension
    fill?: boolean | string | number | { target: number | string; above?: string; below?: string };

    // Line/Point styling
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    borderDash?: number[];
    borderDashOffset?: number;
    borderCapStyle?: CanvasLineCap;
    borderJoinStyle?: CanvasLineJoin;

    // Point styling
    pointBackgroundColor?: string | string[];
    pointBorderColor?: string | string[];
    pointBorderWidth?: number | number[];
    pointRadius?: number | number[];
    pointHoverRadius?: number | number[];
    pointHitRadius?: number | number[];
    pointHoverBackgroundColor?: string | string[];
    pointHoverBorderColor?: string | string[];
    pointHoverBorderWidth?: number | number[];
    pointStyle?: string | string[];
    radius?: number;

    // Bar specific
    barThickness?: number | 'flex';
    maxBarThickness?: number;
    minBarLength?: number;
    barPercentage?: number;
    categoryPercentage?: number;

    // Additional properties
    spanGaps?: boolean;
    showLine?: boolean;
    stepped?: boolean | 'before' | 'after' | 'middle';
    hoverBackgroundColor?: string | string[];
    hoverBorderColor?: string | string[];
    hoverBorderWidth?: number;

    // Allow additional custom properties
    [key: string]: unknown;
}

/**
 * v2-compatible tooltip callback interface
 * In v2, callbacks received (tooltipItem, data), in v3 it's (context)
 */
export interface ChartTooltipCallback {
    beforeTitle?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    title?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    afterTitle?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    beforeBody?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    beforeLabel?(tooltipItem: LegacyTooltipItem, data?: LegacyChartData): string | string[] | void;
    label?(tooltipItem: LegacyTooltipItem, data?: LegacyChartData): string | string[] | void;
    labelColor?(tooltipItem: LegacyTooltipItem, chart?: Chart): { borderColor: string; backgroundColor: string } | void;
    labelTextColor?(tooltipItem: LegacyTooltipItem, chart?: Chart): string | void;
    afterLabel?(tooltipItem: LegacyTooltipItem, data?: LegacyChartData): string | string[] | void;
    afterBody?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    beforeFooter?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    footer?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
    afterFooter?(tooltipItems: LegacyTooltipItem[], data?: LegacyChartData): string | string[] | void;
}

/**
 * Legacy tooltip item structure (v2 style)
 */
export interface LegacyTooltipItem {
    datasetIndex: number;
    index: number;
    x?: number;
    y?: number;
    xLabel?: string | number;
    yLabel?: string | number;
    value?: string;
    label?: string;
}

/**
 * Legacy chart data structure for tooltip callbacks
 */
export interface LegacyChartData {
    labels?: (string | string[])[];
    datasets: Array<{
        label?: string;
        data: unknown[];
        [key: string]: unknown;
    }>;
}

/**
 * v2-compatible tooltip options
 * In v2 this was ChartTooltipOptions, now maps to TooltipOptions with v2 callback structure
 */
export interface ChartTooltipOptions {
    enabled?: boolean;
    custom?: (tooltipModel: unknown) => void;
    mode?: string;
    intersect?: boolean;
    position?: 'average' | 'nearest';
    callbacks?: ChartTooltipCallback;
    filter?: (item: LegacyTooltipItem, data?: LegacyChartData) => boolean;
    itemSort?: (a: LegacyTooltipItem, b: LegacyTooltipItem, data?: LegacyChartData) => number;
    backgroundColor?: string;
    titleFontFamily?: string;
    titleFontSize?: number;
    titleFontStyle?: string;
    titleFontColor?: string;
    titleSpacing?: number;
    titleMarginBottom?: number;
    bodyFontFamily?: string;
    bodyFontSize?: number;
    bodyFontStyle?: string;
    bodyFontColor?: string;
    bodySpacing?: number;
    footerFontFamily?: string;
    footerFontSize?: number;
    footerFontStyle?: string;
    footerFontColor?: string;
    footerSpacing?: number;
    footerMarginTop?: number;
    xPadding?: number;
    yPadding?: number;
    caretPadding?: number;
    caretSize?: number;
    cornerRadius?: number;
    multiKeyBackground?: string;
    displayColors?: boolean;
    borderColor?: string;
    borderWidth?: number;
    rtl?: boolean;
    textDirection?: string;
}

/**
 * v2-compatible hover options
 * In v2 this was ChartHoverOptions
 */
export interface ChartHoverOptions {
    mode?: string | null;
    intersect?: boolean;
    axis?: 'x' | 'y' | 'xy';
    animationDuration?: number;
    onHover?: (event: MouseEvent, elements: unknown[]) => void;
}

/**
 * v2-compatible legend label options
 */
export interface ChartLegendLabelOptions {
    boxWidth?: number;
    fontSize?: number;
    fontStyle?: string;
    fontColor?: string;
    fontFamily?: string;
    padding?: number;
    generateLabels?: (chart: Chart) => unknown[];
    filter?: (legendItem: unknown, chartData: unknown) => boolean;
    usePointStyle?: boolean;
}

/**
 * v2-compatible legend options
 * In v2 this was ChartLegendOptions
 */
export interface ChartLegendOptions {
    display?: boolean;
    position?: PositionType;
    fullWidth?: boolean;
    onClick?: (event: MouseEvent, legendItem: unknown, legend: unknown) => void;
    onHover?: (event: MouseEvent, legendItem?: unknown, legend?: unknown) => void;
    onLeave?: (event: MouseEvent, legendItem?: unknown, legend?: unknown) => void;
    reverse?: boolean;
    labels?: ChartLegendLabelOptions;
    rtl?: boolean;
    textDirection?: string;
}

/**
 * v2-compatible scale label options (renamed to 'title' in v3)
 */
export interface ChartScaleLabelOptions {
    display?: boolean;
    labelString?: string;
    fontSize?: number;
    fontStyle?: string;
    fontColor?: string;
    fontFamily?: string;
    lineHeight?: number | string;
    padding?: number | { top?: number; bottom?: number };
}

/**
 * v2-compatible tick options
 * Note: callback signature is permissive to support both v2 and v3 styles
 */
export interface ChartTickOptions {
    min?: number;
    max?: number;
    beginAtZero?: boolean;
    suggestedMin?: number;
    suggestedMax?: number;
    stepSize?: number;
    maxTicksLimit?: number;
    fontSize?: number;
    fontStyle?: string;
    fontColor?: string;
    fontFamily?: string;
    padding?: number;
    display?: boolean;
    autoSkip?: boolean;
    autoSkipPadding?: number;
    labelOffset?: number;
    // Permissive callback signature to support both v2 and v3 styles

    callback?: (value: any, index?: any, values?: any) => string | number | null | undefined;
    mirror?: boolean;
    reverse?: boolean;
}

/**
 * v2-compatible grid line options (renamed from 'gridLines' to 'grid' in v3)
 */
export interface ChartGridLineOptions {
    display?: boolean;
    color?: string | string[];
    borderDash?: number[];
    borderDashOffset?: number;
    lineWidth?: number | number[];
    drawBorder?: boolean;
    drawOnChartArea?: boolean;
    drawTicks?: boolean;
    tickMarkLength?: number;
    zeroLineWidth?: number;
    zeroLineColor?: string;
    zeroLineBorderDash?: number[];
    zeroLineBorderDashOffset?: number;
    offsetGridLines?: boolean;
    circular?: boolean;
}

/**
 * v2-compatible time scale options
 */
export interface ChartTimeOptions {
    displayFormats?: {
        [key: string]: string;
    };
    isoWeekday?: boolean | number;
    max?: string | number | Date;
    min?: string | number | Date;
    parser?: string | ((value: unknown) => number);
    round?: string;
    tooltipFormat?: string;
    unit?: string;
    minUnit?: string;
    stepSize?: number;
}

/**
 * v2-compatible X axis configuration (array-based in v2, keyed in v3)
 */
export interface ChartXAxe {
    id?: string;
    type?: string;
    display?: boolean;
    position?: 'top' | 'bottom';
    offset?: boolean;
    gridLines?: ChartGridLineOptions;
    scaleLabel?: ChartScaleLabelOptions;
    ticks?: ChartTickOptions;
    time?: ChartTimeOptions;
    stacked?: boolean;
    barPercentage?: number;
    categoryPercentage?: number;
    barThickness?: number | 'flex';
    maxBarThickness?: number;
    minBarLength?: number;
}

/**
 * v2-compatible Y axis configuration (array-based in v2, keyed in v3)
 */
export interface ChartYAxe {
    id?: string;
    type?: string;
    display?: boolean;
    position?: 'left' | 'right';
    offset?: boolean;
    gridLines?: ChartGridLineOptions;
    scaleLabel?: ChartScaleLabelOptions;
    ticks?: ChartTickOptions;
    stacked?: boolean;
    // Scale callbacks (used by plugins)
    afterDataLimits?: (scale: unknown) => void;
    beforeFit?: (scale: unknown) => void;
    afterFit?: (scale: unknown) => void;
    afterBuildTicks?: (scale: unknown) => void;
}

/**
 * v2-compatible scales configuration (array-based)
 */
export interface ChartScalesSettings {
    xAxes?: ChartXAxe[];
    yAxes?: ChartYAxe[];
}

/**
 * v2-compatible title options (moved to plugins.title in v3)
 */
export interface ChartTitleOptions {
    display?: boolean;
    text?: string | string[];
    position?: PositionType;
    fontSize?: number;
    fontStyle?: string;
    fontColor?: string;
    fontFamily?: string;
    padding?: number;
    lineHeight?: number | string;
}

/**
 * v2-compatible animation options
 */
export interface ChartAnimationOptions {
    duration?: number;
    easing?: string;
    onProgress?: (animation: unknown) => void;
    onComplete?: (animation: unknown) => void;
}

/**
 * v2-compatible layout padding options
 */
export interface ChartLayoutPadding {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
}

/**
 * v2-compatible layout options
 */
export interface ChartLayoutOptions {
    padding?: number | ChartLayoutPadding;
}

/**
 * v2-compatible chart options structure
 * This interface represents the v2 options structure that will be transformed to v3
 */
export interface ChartOptionsV2 {
    // Layout
    layout?: ChartLayoutOptions;

    // v2 top-level options (moved to plugins in v3)
    legend?: ChartLegendOptions;
    title?: ChartTitleOptions;
    tooltips?: ChartTooltipOptions;

    // Scales (array-based in v2)
    scales?: ChartScalesSettings;

    // Interaction
    hover?: ChartHoverOptions;
    onHover?: (event: MouseEvent, elements: unknown[]) => void;
    onClick?: (event: MouseEvent, elements: unknown[]) => void;

    // Animation
    animation?: ChartAnimationOptions | boolean;

    // Responsiveness
    responsive?: boolean;
    maintainAspectRatio?: boolean;
    aspectRatio?: number;

    // Doughnut/Pie specific (v2)
    cutoutPercentage?: number;
    rotation?: number;
    circumference?: number;

    // Custom options (preserved through transformation)
    centerText?: unknown;
    backgroundAreas?: unknown[];
    horizontalLines?: unknown[];
    verticalLines?: {
        lines: unknown[];
        timeScale?: string[];
        events?: {
            rendered?: (position: unknown, index: number) => void;
            mouseover?: (data: unknown, index: number) => void;
        };
    };
    drawLabelOnChart?: boolean;

    // Allow additional custom properties
    [key: string]: unknown;
}

/**
 * Extended ChartOptions that includes v2 properties for backwards compatibility
 * This is the main type used by the existing codebase
 */
export type ChartOptions = ChartOptionsV2;

/**
 * v2-compatible chart type including 'horizontalBar' which was removed in v3
 */
export type ChartType = ChartJsType | 'horizontalBar';

/**
 * Mapping of v2 chart types to v3 equivalents
 */
export const CHART_TYPE_MAP: Record<string, ChartJsType> = {
    horizontalBar: 'bar',
    // Add more mappings if needed
};

/**
 * Check if a chart type is a v2-only type that needs transformation
 */
export function isLegacyChartType (type: string): boolean {
    return type in CHART_TYPE_MAP;
}

/**
 * Get the v3 equivalent of a v2 chart type
 */
export function getV3ChartType (type: string): ChartJsType {
    return CHART_TYPE_MAP[type] || type as ChartJsType;
}
